import React, { useCallback, useState } from "react";
import { useDropzone } from "react-dropzone";
import { Upload, AlertCircle } from "lucide-react";
import { useImportStore } from "../../store/useImportStore";
import { LoadingSpinner } from "../common/LoadingSpinner";
import { cn } from "../../utils/helpers";

export const FileUpload: React.FC = () => {
  const { previewFile, setCurrentStep, loading } = useImportStore();

  const [uploadError, setUploadError] = useState<string | null>(null);

  const onDrop = useCallback(
    async (acceptedFiles: File[]) => {
      const file = acceptedFiles[0];
      if (!file) return;

      setUploadError(null);

      try {
        // Use the store's previewFile method which uses ApiService
        await previewFile(file);
        setCurrentStep("preview");
      } catch (error: unknown) {
        console.error("Error uploading file:", error);
        const errorMessage =
          error instanceof Error ? error.message : "Failed to upload file";
        setUploadError(errorMessage);
      }
    },
    [previewFile, setCurrentStep]
  );

  const { getRootProps, getInputProps, isDragActive, isDragReject } =
    useDropzone({
      onDrop,
      accept: {
        "text/csv": [".csv"],
        "application/vnd.ms-excel": [".csv"],
      },
      maxFiles: 1,
      maxSize: 100 * 1024 * 1024, // 100MB
      disabled: loading,
    });

  return (
    <div className="p-8">
      <div className="text-center mb-6">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">
          Import GPS Data from CSV
        </h2>
        <p className="text-gray-600">
          Upload your CSV file containing historical GPS coordinates to import
          into the system.
        </p>
      </div>

      {/* File Upload Area */}
      <div
        {...getRootProps()}
        className={cn(
          "border-2 border-dashed rounded-lg p-12 text-center cursor-pointer transition-colors",
          isDragActive && !isDragReject && "border-primary-400 bg-primary-50",
          isDragReject && "border-red-400 bg-red-50",
          !isDragActive && "border-gray-300 hover:border-gray-400",
          loading && "cursor-not-allowed opacity-50"
        )}
      >
        <input {...getInputProps()} />

        {loading ? (
          <div className="flex flex-col items-center">
            <LoadingSpinner size="lg" className="mb-4" />
            <p className="text-lg font-medium text-gray-900 mb-2">
              Processing your file...
            </p>
            <p className="text-gray-600">
              Analyzing CSV structure and generating preview
            </p>
          </div>
        ) : (
          <div className="flex flex-col items-center">
            <Upload className="w-16 h-16 text-gray-400 mb-4" />
            <p className="text-lg font-medium text-gray-900 mb-2">
              {isDragActive
                ? isDragReject
                  ? "Invalid file type"
                  : "Drop your CSV file here"
                : "Click to upload or drag and drop"}
            </p>
            <p className="text-gray-600 mb-4">CSV files up to 100MB</p>
            <p className="text-sm text-gray-500">Supported formats: .csv</p>
          </div>
        )}
      </div>

      {/* Error Message */}
      {uploadError && (
        <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg">
          <div className="flex items-center">
            <AlertCircle className="w-5 h-5 text-red-500 mr-2" />
            <p className="text-red-700">{uploadError}</p>
          </div>
        </div>
      )}

      {/* File Requirements */}
      <div className="mt-8 bg-gray-50 rounded-lg p-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">
          CSV File Requirements
        </h3>
        <ul className="space-y-2 text-sm text-gray-600">
          <li className="flex items-start">
            <span className="text-green-500 mr-2">•</span>
            <span>
              <strong>Required columns:</strong> Bike/Device ID, Latitude,
              Longitude, Timestamp
            </span>
          </li>
          <li className="flex items-start">
            <span className="text-green-500 mr-2">•</span>
            <span>
              <strong>Optional columns:</strong> Speed, Battery Level,
              Temperature, etc.
            </span>
          </li>
          <li className="flex items-start">
            <span className="text-green-500 mr-2">•</span>
            <span>
              <strong>Timestamp format:</strong> ISO 8601 (YYYY-MM-DD HH:MM:SS)
              or Unix timestamp
            </span>
          </li>
          <li className="flex items-start">
            <span className="text-green-500 mr-2">•</span>
            <span>
              <strong>Coordinates:</strong> Decimal degrees (e.g., 40.7589,
              -73.9851)
            </span>
          </li>
          <li className="flex items-start">
            <span className="text-green-500 mr-2">•</span>
            <span>
              <strong>File size:</strong> Maximum 100MB per file
            </span>
          </li>
        </ul>
      </div>

      {/* Sample CSV Format */}
      <div className="mt-6 bg-gray-50 rounded-lg p-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">
          Example CSV Format
        </h3>
        <div className="bg-white rounded border p-3 font-mono text-sm overflow-x-auto">
          <div className="text-gray-700">
            bike_id,latitude,longitude,timestamp,speed,battery_level
            <br />
            BIKE001,40.7589,-73.9851,2024-01-15 10:30:00,15.5,85
            <br />
            BIKE001,40.7592,-73.9848,2024-01-15 10:30:30,18.2,84
            <br />
            BIKE002,40.7601,-73.9901,2024-01-15 10:31:00,0.0,92
          </div>
        </div>
      </div>
    </div>
  );
};
